import React from 'react';

import styles from './three-segment-year-tile.module.scss';

export type ThreeSegmentKey = 'j1Only' | 'j2Only' | 'j1j2';

export type ThreeSegmentYearRow = {
  year: number,
  j1Only: number,
  j2Only: number,
  j1j2: number,
};

type Props = {
  title: string,
  rows: {
    primary: ThreeSegmentYearRow,
    secondary: ThreeSegmentYearRow,
  },
  labels?: {
    j1: string,
    j2: string,
    j1j2: string,
  },
  /** Inclut les inscriptions J1+J2 dans J1 et J2 ; affiche 2 segments et un total J1+J2. */
  mergeBothDaysIntoJ1AndJ2?: boolean,
};

const getFlexWeight = (value: number) => Math.max(1, value);

const toMergedDayCounts = (row: ThreeSegmentYearRow) => ({
  j1: row.j1Only + row.j1j2,
  j2: row.j2Only + row.j1j2,
});

const getMergedHeaderWeights = (
  primary: ThreeSegmentYearRow,
  secondary: ThreeSegmentYearRow,
) => {
  const primaryCounts = toMergedDayCounts(primary);
  const secondaryCounts = toMergedDayCounts(secondary);
  return {
    j1: Math.max(primaryCounts.j1, secondaryCounts.j1),
    j2: Math.max(primaryCounts.j2, secondaryCounts.j2),
  };
};

const getThreeSegmentHeaderWeights = (
  primary: ThreeSegmentYearRow,
  secondary: ThreeSegmentYearRow,
) => ({
  j1Only: Math.max(primary.j1Only, secondary.j1Only),
  j2Only: Math.max(primary.j2Only, secondary.j2Only),
  j1j2: Math.max(primary.j1j2, secondary.j1j2),
});

export default function ThreeSegmentYearTile({
  title,
  rows,
  labels = { j1: 'J1', j2: 'J2', j1j2: 'J1+J2' },
  mergeBothDaysIntoJ1AndJ2 = false,
}: Props) {
  const mergedHeaderWeights = getMergedHeaderWeights(rows.primary, rows.secondary);
  const threeSegmentHeaderWeights = getThreeSegmentHeaderWeights(rows.primary, rows.secondary);

  const renderRow = (row: ThreeSegmentYearRow, muted?: boolean) => {
    if (mergeBothDaysIntoJ1AndJ2) {
      const { j1, j2 } = toMergedDayCounts(row);
      const total = j1 + j2;

      return (
        <div className={muted ? `${styles.row} ${styles.muted}` : styles.row}>
          <span className={styles.year}>{row.year}</span>
          <div className={styles.bars}>
            <div className={styles.segmentJ1} style={{ flexGrow: getFlexWeight(j1) }}>
              {j1}
            </div>
            <div className={styles.segmentJ2} style={{ flexGrow: getFlexWeight(j2) }}>
              {j2}
            </div>
          </div>
          <span className={styles.total}>{total}</span>
        </div>
      );
    }

    const total = row.j1Only + row.j2Only + row.j1j2;

    return (
      <div className={muted ? `${styles.row} ${styles.muted}` : styles.row}>
        <span className={styles.year}>{row.year}</span>
        <div className={styles.bars}>
          <div className={styles.segmentJ1} style={{ flexGrow: getFlexWeight(row.j1Only) }}>
            {row.j1Only}
          </div>
          <div className={styles.segmentJ2} style={{ flexGrow: getFlexWeight(row.j2Only) }}>
            {row.j2Only}
          </div>
          <div className={styles.segmentJ1J2} style={{ flexGrow: getFlexWeight(row.j1j2) }}>
            {row.j1j2}
          </div>
        </div>
        <span className={styles.total}>{total}</span>
      </div>
    );
  };

  return (
    <section className={styles.tile}>
      <h2 className={styles.title}>{title}</h2>
      <div className={styles.body}>
        <div className={styles.header}>
          <span className={styles.headerSpacer} />
          <div className={styles.pills}>
            {mergeBothDaysIntoJ1AndJ2 ? (
              <>
                <div style={{ flexGrow: getFlexWeight(mergedHeaderWeights.j1) }}>
                  <span className={styles.pillJ1}>{labels.j1}</span>
                </div>
                <div style={{ flexGrow: getFlexWeight(mergedHeaderWeights.j2) }}>
                  <span className={styles.pillJ2}>{labels.j2}</span>
                </div>
              </>
            ) : (
              <>
                <div style={{ flexGrow: getFlexWeight(threeSegmentHeaderWeights.j1Only) }}>
                  <span className={styles.pillJ1}>{labels.j1}</span>
                </div>
                <div style={{ flexGrow: getFlexWeight(threeSegmentHeaderWeights.j2Only) }}>
                  <span className={styles.pillJ2}>{labels.j2}</span>
                </div>
                <div style={{ flexGrow: getFlexWeight(threeSegmentHeaderWeights.j1j2) }}>
                  <span className={styles.pillJ1J2}>{labels.j1j2}</span>
                </div>
              </>
            )}
          </div>
          <span className={styles.headerSpacer} />
        </div>

        <div className={styles.rows}>
          {renderRow(rows.primary)}
          {renderRow(rows.secondary, true)}
        </div>
      </div>
    </section>
  );
}
